import React, { Suspense, lazy, useContext, useEffect } from 'react';
import {
  Route,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
  Link
} from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import './App.css';
import ToastProvider from './components/ui/ToastProvider';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import ClientSeo from './components/seo/ClientSeo';
import { usePublicTheme } from './hooks/usePublicTheme';

// Home stays eager — it's the LCP route.
import Home from './pages/Home';

const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Documents = lazy(() => import('./pages/Documents'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Lessons = lazy(() => import('./pages/Lessons'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const Login = lazy(() => import('./pages/Login'));
const AdminRoutes = lazy(() => import('./pages/admin/AdminRoutes'));

const RouteFallback = () => (
  <div className="route-transition-fallback" aria-hidden="true" />
);

const prefetchPublicRoutes = () => {
  // Skip Gallery here — it's the heaviest chunk and hurts homepage Lighthouse.
  import('./pages/About');
  import('./pages/Contact');
  import('./pages/Documents');
  import('./pages/Bookings');
  import('./pages/Lessons');
};

// Create router with future flags
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Admin Routes - rendered with their own layout, outside the public
          Navbar/Footer chrome. */}
      <Route path="admin/*" element={
        <Suspense fallback={<RouteFallback />}>
          <AdminRoutes />
        </Suspense>
      } />

      {/* Public site */}
      <Route path="/" element={<AppLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="contact" element={<Contact />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="documents" element={<Documents />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="lessons" element={<Lessons />} />

        {/* Auth Routes */}
        <Route path="login" element={<Login />} />
        <Route path="unauthorized" element={<Unauthorized />} />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

// Shown to visitors when maintenance mode is on (admins bypass it).
function MaintenanceScreen({ siteName }) {
  return (
    <div className="app">
      <main className="main-content">
        <div className="container" style={{ textAlign: 'center', padding: '6rem 1rem' }}>
          <h1>{siteName || 'Site'} is under maintenance</h1>
          <p>We're making some improvements and will be back shortly. Thanks for your patience.</p>
          <p style={{ marginTop: '2rem' }}>
            <Link to="/login" className="btn btn-primary">Admin Login</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

// App Layout component
function AppLayout() {
  const { settings } = useSettings();
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  const isThemePreviewEmbed = new URLSearchParams(location.search).get('themePreview') === '1';

  usePublicTheme();

  useEffect(() => {
    if (isThemePreviewEmbed) {
      document.documentElement.classList.add('theme-preview-embed');
    } else {
      document.documentElement.classList.remove('theme-preview-embed');
    }

    return () => {
      document.documentElement.classList.remove('theme-preview-embed');
    };
  }, [isThemePreviewEmbed]);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetchPublicRoutes);
      return undefined;
    }

    const timer = window.setTimeout(prefetchPublicRoutes, 5000);
    return () => window.clearTimeout(timer);
  }, []);

  // In maintenance mode, hide the public site from non-admins, but always
  // allow the login page so the admin can get in.
  if (settings.maintenanceMode && !isAuthenticated && location.pathname !== '/login') {
    return <MaintenanceScreen siteName={settings.siteName} />;
  }

  return (
    <div className="app">
      <ClientSeo />
      <ScrollToTop />
      <Navbar />
      <main className="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
